<script setup lang="ts">
import DoNotContactMessagingInfo from '@/components/Conversations/DoNotContact/DoNotContactMessagingInfo.vue';
import { computed } from 'vue';
import MessageList from '@/components/Conversations/Message/MessageList.vue';
import MobileScoreBlurb from '@/components/Mobile/Conversation/MobileScoreBlurb.vue';
import MobileMessageComposer from '@/components/Mobile/Conversation/MobileMessageComposer.vue';
import OptedOutMessagingPanel from '@/components/Conversations/Message/OptedOutMessagingPanel.vue';
import { useConversationThread } from '@/composables/useConversationThread';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';

const conversationIndexService = new ConversationIndexService();

// Mobile thread orchestration (load, pagination, 5s poll, scroll, mark-read)
// lives in the composable; this component only renders it. mark-read fires
// inside fetchConversationMessages on the page-1 load the composable triggers.
// aiRecruiterName (the Copilot display name MessageList needs) is resolved by
// the composable, so this component just forwards it.
const {
  messagesViewElementRef,
  messageList,
  doNotContactHistoryUnavailable,
  jobApplicant,
  isMessageListVisible,
  userProfileId,
  currentProId,
  aiRecruiterName,
  handleScroll,
  sendMessage: sendThreadMessage,
} = useConversationThread();

const isDoNotContact = computed(() => conversationIndexService.isMessagingDoNotContact);
const refreshState = computed(() => conversationIndexService.messageStatusRefreshState);
const showDisabledComposer = computed(() => isDoNotContact.value || refreshState.value !== 'idle');
const isMessagingDisabled = computed(() => conversationIndexService.isMessagingDisabled);
const retryMessageStatus = () => conversationIndexService.refreshSelectedProMessageStatus(true);

async function sendMessage(message: string) {
  if (isMessagingDisabled.value) throw new Error('Messaging is disabled');
  await sendThreadMessage(message);
}

// The candidate's name feeds the composer's "Message <FirstName>" placeholder.
const candidateName = computed(() => conversationIndexService.selectedProName);

// Mirror the desktop gate (conversation-tab.vue): hide the composer and explain
// why when the candidate has opted out. selectedProMessageStatus is populated by
// the shared selectConversationIndex flow, so it is available on mobile too.
const canReceiveMessage = computed(
  () =>
    !conversationIndexService.selectedProMessageStatus ||
    conversationIndexService.selectedProMessageStatus.canReceiveMessage,
);
</script>

<template>
  <div class="flex h-full flex-col bg-tint-0" data-test="mobile-conversation">
    <div class="relative h-0 flex-grow overflow-hidden">
      <!-- overflow-x-hidden matters: overflow-y alone computes overflow-x to auto, so
           any over-wide turn would make the whole thread pan sideways on phones. -->
      <div
        ref="messagesViewElementRef"
        :class="{
          'h-full overflow-y-auto overflow-x-hidden pb-8 transition-opacity': true,
          'opacity-0': !isMessageListVisible,
          'opacity-100': isMessageListVisible,
        }"
        data-test="mobile-conversation-scroll"
        @scroll="handleScroll"
      >
        <MessageList
          v-if="userProfileId"
          :messages="messageList"
          :do-not-contact-history-unavailable="doNotContactHistoryUnavailable"
          :current-pro-id="currentProId"
          :job-applicant="jobApplicant"
          :ai-recruiter-name="aiRecruiterName"
        >
          <!-- Swap the desktop score bubble for the dedicated mobile card. MessageList
               only renders this slot for SCORE_BLURB turns, so the metadata type is
               already guaranteed. Unlike the desktop bubble it does not gate on
               jobTitle, so a score turn without a job title still renders. -->
          <template #score-blurb="{ message }">
            <MobileScoreBlurb
              v-if="message.metadata.applicationId != null"
              class="my-2"
              :application-id="message.metadata.applicationId"
              :job-title="message.metadata.jobTitle"
              :ai-recruiter-name="aiRecruiterName"
              :score="Number(message.metadata.score)"
              :content="message.content"
              :chips="message.metadata.chips"
            />
          </template>
        </MessageList>
      </div>

      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-tint-0 via-tint-0/75 to-tint-0/0"
      />
    </div>

    <DoNotContactMessagingInfo
      :do-not-contact="isDoNotContact"
      :refresh-state="refreshState"
      @retry="retryMessageStatus"
    />
    <MobileMessageComposer
      v-if="showDisabledComposer || canReceiveMessage"
      :disabled="isMessagingDisabled"
      :pro-name="candidateName"
      :on-send="sendMessage"
    />
    <OptedOutMessagingPanel v-else />
  </div>
</template>
